import { useMemo } from 'react';

function getColor(score) {
  if (score >= 75) return 'bg-red-500';
  if (score >= 50) return 'bg-orange-400';
  if (score >= 25) return 'bg-amber-300';
  if (score > 0) return 'bg-emerald-300';
  return 'bg-slate-100';
}

function getOpacity(score) {
  if (score === 0) return 'opacity-30';
  return `opacity-${Math.min(100, Math.max(40, Math.round(score)))}`;
}

export default function RiskHeatmap({ zones = [], onZoneClick }) {
  const gridCols = useMemo(() => {
    const count = zones.length;
    if (count <= 4) return 'grid-cols-2';
    if (count <= 9) return 'grid-cols-3';
    return 'grid-cols-4';
  }, [zones.length]);

  if (zones.length === 0) {
    return (
      <div className="glass p-6 text-center">
        <p className="text-sm text-slate-400">No zone risk data available</p>
      </div>
    );
  }

  if (zones.length === 0) {
    return (
      <div className="glass p-6 text-center">
        <p className="text-sm text-slate-400">No zone risk data available</p>
        <p className="text-xs text-slate-500 mt-1">Calculate risk scores to see zone breakdown</p>
      </div>
    );
  }

  return (
    <div className="glass p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Zone Risk Heatmap</h3>
      <div className={`grid ${gridCols} gap-2`}>
        {zones.map((zone) => {
          const score = zone.overallScore || 0;
          const colorClass = getColor(score);
          // Extract zone name from enriched data or fallback to zone object property
          const zoneName = zone.zone?.name || zone.name || zone.zoneName || `Zone ${zone.zoneId || zone.id}`;
          const zoneId = zone.zoneId || zone.id;
          const riskLevel = zone.riskLevel || 'UNKNOWN';

          return (
            <div
              key={zoneId}
              onClick={() => onZoneClick && onZoneClick(zone)}
              className={`relative rounded-lg p-3 ${colorClass} text-white transition-all hover:scale-105 ${onZoneClick ? 'cursor-pointer' : 'cursor-default'}`}
              style={{ opacity: Math.max(0.4, score / 100) }}
              title={`${zoneName}: Risk Score ${Math.round(score)} (${riskLevel})`}
            >
              <div className="text-xs font-bold truncate">
                {zoneName}
              </div>
              <div className="text-2xl font-black mt-1">
                {Math.round(score)}
              </div>
              <div className="text-[10px] opacity-80 mt-0.5">
                {riskLevel}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-500">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-300" />
          <span>Low (0-24)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-amber-300" />
          <span>Medium (25-49)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-orange-400" />
          <span>High (50-74)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-500" />
          <span>Critical (75-100)</span>
        </div>
      </div>
    </div>
  );
}
